import type { Team, FighterBase, FighterInit, Loadout } from './types';
import type { RoundEvent, ActionKind } from './round';
import type { Stance } from './config';
import { maxHpOf, armorPointsOf } from './config';

export const RUSH_MID_MAPS = ['dust2', 'mirage', 'inferno', 'nuke'] as const;
export type RushMidMap = (typeof RUSH_MID_MAPS)[number];

export interface RushMidPublicFighter extends FighterBase {
  hp: number;
  alive: boolean;
  stance: Stance;
  afk?: true;
}

export interface RushMidDropAward {
  itemHashName: string;
  name: string;
  image: string | null;
  rarity?: string;
  rarityColor?: string;
  priceCredits: number;
  userId?: string;
}

export interface RushMidScoreEntry {
  fighterId: string;
  name: string;
  team: Team;
  kills: number;
  pts: number;
  alive: boolean;
  mvp: boolean;
  payout: number;
  ratingDelta?: number;
  afk?: true;
  drop?: RushMidDropAward;
}

export interface RushMidBuyRosterEntry {
  index: number;
  name: string;
  isBot: boolean;
  loadout: Loadout | null;
  rating?: number;
  userId?: string;
}

export interface RushMidPayout {
  pot: number;
  prize: number;
  won: boolean;
  draw: boolean;
  survived: boolean;
  share: number;
  net: number;
  ratingDelta?: number;
}

export type RushMidStatus = 'NONE' | 'PARTY' | 'OPEN' | 'BUYING' | 'PLAYING' | 'OVER';

export type RushMidOpenSeat =
  | { userId: string; name: string; level: number; rating?: number; partyGroup?: number }
  | null;

export interface RushMidSnapshotNone {
  status: 'NONE';
}

export interface RushMidPartyMemberDto {
  userId: string;
  name: string;
  ready: boolean;
  canAfford: boolean;
}

export type RushMidSidePref = Team | 'any';

export interface RushMidSnapshotParty {
  status: 'PARTY';
  code: string;
  isLeader: boolean;
  leaderUserId: string;
  youUserId: string;
  members: RushMidPartyMemberDto[];
  mode: number;
  ticket: number;
  side: RushMidSidePref;
  matchMode?: 'ranked' | 'bots';
  allowedModes: number[];
}

export interface RushMidSnapshotOpen {
  status: 'OPEN';
  matchId: string;
  ticket: number;
  mode: number;
  map: string;
  yourSide: Team;
  seats: { t: RushMidOpenSeat[]; ct: RushMidOpenSeat[] };
  botFillAt?: string;
  partyRole?: 'leader' | 'member';
}

interface RushMidMatchBase {
  matchId: string;
  mode: number;
  ticket: number;
  map: RushMidMap;
  matchMode?: 'ranked' | 'bots';
}

export interface RushMidSnapshotBuying extends RushMidMatchBase {
  status: 'BUYING';
  side: Team;
  allies: RushMidBuyRosterEntry[];
  enemies: RushMidBuyRosterEntry[];
  buyDeadline: number;
  serverNow?: number;
  yourLoadout?: Loadout | null;
  yourReady?: boolean;
}

export interface RushMidRoundAction {
  kind: ActionKind;
  stance?: Stance;
}

interface RushMidRoundBase extends RushMidMatchBase {
  side?: Team;
  round: number;
  inits: FighterInit[];
  fighters: RushMidPublicFighter[];
  lastEvents: RoundEvent[];
  lastActions?: Record<string, RushMidRoundAction>;
  yourFighterId: string | null;
}

export interface RushMidSnapshotPlaying extends RushMidRoundBase {
  status: 'PLAYING';
  roundDeadline: number;
  yourActionSubmitted: boolean;
}

export interface RushMidSnapshotOver extends RushMidRoundBase {
  status: 'OVER';
  winner: Team | null;
  payout?: RushMidPayout;
  scoreboard: RushMidScoreEntry[];
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
}

export type RushMidSnapshot = (
  | RushMidSnapshotNone
  | RushMidSnapshotParty
  | RushMidSnapshotOpen
  | RushMidSnapshotBuying
  | RushMidSnapshotPlaying
  | RushMidSnapshotOver
) & { stateVersion?: number };

// WHY: any new event field that moves HP/kit must be folded here too, or the replay drifts from the battle
export function initialFightersState(
  inits: FighterInit[],
  teamA: Loadout[],
  teamB: Loadout[],
): Record<string, RushMidPublicFighter> {
  const state: Record<string, RushMidPublicFighter> = {};
  for (const i of inits) {
    const l = (i.team === 't' ? teamA : teamB)[i.index];
    const maxHp = maxHpOf(l);
    state[i.id] = {
      id: i.id, team: i.team, hp: maxHp, maxHp, armor: armorPointsOf(l),
      hasGrenade: l.grenade, hasSmoke: !!l.smoke, hasMedkit: !!l.medkit, hasFlash: !!l.flash,
      alive: true, stance: 'attack',
    };
  }
  return state;
}

export function applyRoundEvent(state: Record<string, RushMidPublicFighter>, ev: RoundEvent): void {
  const hit = (id: string, dmg: number) => {
    const f = state[id];
    if (f) f.hp = Math.max(0, f.hp - dmg);
  };
  const setArmor = (id: string, armor: number) => {
    const f = state[id];
    if (f) f.armor = armor;
  };
  if (ev.type === 'shot') {
    if (ev.hit) hit(ev.target, ev.damage - (ev.absorbed ?? 0));
    setArmor(ev.target, ev.armor);
    if (state[ev.shooter]) state[ev.shooter].stance = ev.stance;
    if (ev.reflect) hit(ev.shooter, ev.reflect - (ev.reflectAbsorbed ?? 0));
    if (ev.reflectArmor !== undefined) setArmor(ev.shooter, ev.reflectArmor);
  } else if (ev.type === 'knife') {
    hit(ev.target, ev.damage - (ev.absorbed ?? 0));
    setArmor(ev.target, ev.armor);
    if (ev.punish) hit(ev.shooter, ev.punish - (ev.punishAbsorbed ?? 0));
    if (ev.punishArmor !== undefined) setArmor(ev.shooter, ev.punishArmor);
  } else if (ev.type === 'grenade') {
    if (state[ev.shooter]) state[ev.shooter].hasGrenade = false;
    for (const h of ev.hits) {
      hit(h.id, h.damage - (h.absorbed ?? 0));
      setArmor(h.id, h.armor);
    }
  } else if (ev.type === 'heal') {
    const f = state[ev.id];
    if (f) { f.hp = Math.min(f.maxHp, f.hp + ev.amount); f.hasMedkit = false; }
  } else if (ev.type === 'smoke') {
    const f = state[ev.shooter];
    if (f) f.hasSmoke = false;
  } else if (ev.type === 'flash') {
    const f = state[ev.shooter];
    if (f) f.hasFlash = false;
  } else if (ev.type === 'shield') {
     
  } else if (ev.type === 'death') {
    const f = state[ev.id];
    if (f) { f.hp = 0; f.alive = false; }
  }
}

export function foldRoundEvents(
  inits: FighterInit[],
  teamA: Loadout[],
  teamB: Loadout[],
  rounds: RoundEvent[][],
  upto: number,
): RushMidPublicFighter[] {
  const state = initialFightersState(inits, teamA, teamB);
  for (let r = 0; r <= upto && r < rounds.length; r++) {
    for (const ev of rounds[r]) applyRoundEvent(state, ev);
  }
  return Object.values(state);
}


interface RushMidSpectateBase {
  matchId: string;
  mode: number;
  ticket: number;
  map: RushMidMap;
  round: number;
  inits: FighterInit[];
  fighters: RushMidPublicFighter[];
  lastEvents: RoundEvent[];
  lastActions?: Record<string, RushMidRoundAction>;
}

export interface RushMidSpectatePlaying extends RushMidSpectateBase {
  status: 'PLAYING';
  roundDeadline: number;
}

export interface RushMidSpectateOver extends RushMidSpectateBase {
  status: 'OVER';
  winner: Team | null;
  scoreboard: RushMidScoreEntry[];
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
}

export type RushMidSpectateView = (RushMidSpectatePlaying | RushMidSpectateOver) & { stateVersion?: number };

export interface RushMidLiveGame {
  matchId: string;
  ticket: number;
  mode: number;
  round: number;
  seats: { t: RushMidOpenSeat[]; ct: RushMidOpenSeat[] };
}

export const isSpectatePlaying = (v: RushMidSpectateView): v is RushMidSpectatePlaying & { stateVersion?: number } =>
  v.status === 'PLAYING';
export const isSpectateOver = (v: RushMidSpectateView): v is RushMidSpectateOver & { stateVersion?: number } =>
  v.status === 'OVER';

export const isPartySnapshot = (s: RushMidSnapshot): s is RushMidSnapshotParty => s.status === 'PARTY';
export const isBuyingSnapshot = (s: RushMidSnapshot): s is RushMidSnapshotBuying => s.status === 'BUYING';
export const isPlayingSnapshot = (s: RushMidSnapshot): s is RushMidSnapshotPlaying => s.status === 'PLAYING';
export const isOpenSnapshot = (s: RushMidSnapshot): s is RushMidSnapshotOpen => s.status === 'OPEN';
export const isOverSnapshot = (s: RushMidSnapshot): s is RushMidSnapshotOver => s.status === 'OVER';
export const isRoundSnapshot = (s: RushMidSnapshot): s is RushMidSnapshotPlaying | RushMidSnapshotOver =>
  s.status === 'PLAYING' || s.status === 'OVER';
